"use client";

import { Turnstile } from "@marsidev/react-turnstile";
import { useTranslations } from "next-intl";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

const TURNSTILE_SITE_KEY =
	process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || "0x4AAAAAAENuL2ZEAIk_5jbE";
const TOKEN_WAIT_TIMEOUT_MILLISECONDS = 30_000;

export type TurnstileAction = "chart" | "material_upload";

export type CaptchaComponentRef = {
	getToken: () => string;
	getFreshToken: (action?: TurnstileAction) => Promise<string>;
	reset: (action?: TurnstileAction) => void;
};

type CaptchaComponentProps = {
	action: TurnstileAction;
	className?: string;
	onTokenChange?: (captchaToken: string) => void;
	onVerificationFailed?: () => void;
};

type PendingTokenRequest = {
	resolve: (captchaToken: string) => void;
	reject: (error: Error) => void;
	timeout: ReturnType<typeof setTimeout>;
};

const CaptchaComponent = forwardRef<CaptchaComponentRef, CaptchaComponentProps>(
	({ action, className = "", onTokenChange, onVerificationFailed }, forwardedRef) => {
		const translate = useTranslations("form");
		const captchaTokenRef = useRef("");
		const activeActionRef = useRef(action);
		const generationRef = useRef(0);
		const pendingRequestRef = useRef<PendingTokenRequest | null>(null);
		const [captchaToken, setCaptchaToken] = useState("");
		const [hasError, setHasError] = useState(false);
		const [widget, setWidget] = useState({ action, generation: 0 });

		const updateCaptchaToken = useCallback(
			(nextToken: string) => {
				captchaTokenRef.current = nextToken;
				setCaptchaToken(nextToken);
				onTokenChange?.(nextToken);
			},
			[onTokenChange],
		);

		const clearPendingRequest = useCallback(() => {
			const pendingRequest = pendingRequestRef.current;
			if (pendingRequest) clearTimeout(pendingRequest.timeout);
			pendingRequestRef.current = null;
			return pendingRequest;
		}, []);

		const resetCaptcha = useCallback(
			(nextAction = activeActionRef.current) => {
				clearPendingRequest()?.reject(new Error("CAPTCHA request was reset."));
				updateCaptchaToken("");
				setHasError(false);
				activeActionRef.current = nextAction;
				generationRef.current += 1;
				setWidget({ action: nextAction, generation: generationRef.current });
			},
			[clearPendingRequest, updateCaptchaToken],
		);

		useImperativeHandle(
			forwardedRef,
			() => ({
				getToken: () => captchaTokenRef.current,
				getFreshToken: (nextAction = activeActionRef.current) => {
					resetCaptcha(nextAction);
					return new Promise<string>((resolve, reject) => {
						pendingRequestRef.current = {
							resolve,
							reject,
							timeout: setTimeout(() => {
								clearPendingRequest();
								setHasError(true);
								reject(new Error("Timed out while waiting for human verification."));
							}, TOKEN_WAIT_TIMEOUT_MILLISECONDS),
						};
					});
				},
				reset: resetCaptcha,
			}),
			[clearPendingRequest, resetCaptcha],
		);

		useEffect(() => {
			if (action !== activeActionRef.current) resetCaptcha(action);
		}, [action, resetCaptcha]);

		useEffect(
			() => () => {
				clearPendingRequest()?.reject(new Error("CAPTCHA component was unmounted."));
			},
			[clearPendingRequest],
		);

		const handleFailure = () => {
			if (widget.generation !== generationRef.current) return;
			updateCaptchaToken("");
			setHasError(true);
			clearPendingRequest()?.reject(new Error("Human verification failed."));
			onVerificationFailed?.();
		};

		return (
			<div className={`min-h-[70px] w-full ${className}`}>
				<Turnstile
					key={`${widget.action}-${widget.generation}`}
					siteKey={TURNSTILE_SITE_KEY}
					onSuccess={(nextToken) => {
						// Ignore callbacks from a widget replaced for another request/action.
						if (widget.generation !== generationRef.current) return;
						updateCaptchaToken(nextToken);
						setHasError(false);
						clearPendingRequest()?.resolve(nextToken);
					}}
					onExpire={() => {
						if (widget.generation === generationRef.current) resetCaptcha();
					}}
					onError={handleFailure}
					onTimeout={handleFailure}
					onUnsupported={handleFailure}
					options={{
						action: widget.action,
						appearance: "always",
						refreshExpired: "manual",
						refreshTimeout: "manual",
						retry: "never",
						responseField: false,
						size: "flexible",
					}}
				/>
				{hasError && (
					<div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-red-600" role="alert">
						<span>{translate("captchaLoadFailed")}</span>
						<button
							type="button"
							onClick={() => resetCaptcha()}
							className="font-medium text-main underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-main"
						>
							{translate("captchaRetry")}
						</button>
					</div>
				)}
				<span className="sr-only" aria-live="polite">
					{translate(captchaToken ? "captchaCompleted" : "captchaRequired")}
				</span>
			</div>
		);
	},
);

CaptchaComponent.displayName = "CaptchaComponent";

export default CaptchaComponent;
